import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, Stack } from "@mui/material";
import { Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate } from "react-router-dom";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import { imprimirComprobanteGasto } from "lib/comprobantegasto";

function Agregargasto({ embedded = false, onExit, onSaved } = {}) {
  const navigate = useNavigate();
  const { post } = useApi();

  const [fecha, setFecha] = useState(new Date().toISOString().split("T")[0]);
  // Selección cerrada: el backend une la lista estándar con las categorías
  // ya usadas; el texto libre solo ensuciaba el catálogo con typos.
  const [categoria, setCategoria] = useState(null);
  const [categoriasList, setCategoriasList] = useState([]);
  // Categorías que exigen chasis (flag del catálogo): el gasto queda ligado
  // al motor en gastos.referencia, que es lo que suman Taller y los informes.
  const [conChasis, setConChasis] = useState(() => new Set());
  const [chasis, setChasis] = useState(null);
  const [chasisOpciones, setChasisOpciones] = useState([]);
  const requiereChasis = Boolean(categoria) && conChasis.has(categoria);
  // Forma de pago: solo en negocios con contabilidad (define si el asiento
  // acredita caja o banco). El cuadre de caja no la usa.
  const [formasPago, setFormasPago] = useState([]);
  const [formaPago, setFormaPago] = useState(null);
  const [descripcion, setDescripcion] = useState("");
  const [valor, setValor] = useState("");
  const [sucursal, setSucursal] = useState(null);
  const sucursalList = useSucursales();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    post("/api/buscarcategoriasdegasto").then(({ status, data }) => {
      if (status === 200) {
        const cats = (data?.data1 || []).map((c) => String(c)).filter(Boolean);
        setCategoriasList(cats);
        setConChasis(
          new Set((data?.categorias || []).filter((c) => c.requierechasis).map((c) => c.nombre))
        );
        const formas = data?.formaspago || [];
        setFormasPago(formas);
        setFormaPago(formas.find((f) => /^(efectivo|contado)$/i.test(f)) || null);
      }
    });
  }, []);

  function buscarChasis(texto) {
    const q = String(texto || "").trim();
    if (q.length < 3) { setChasisOpciones([]); return; }
    post("/api/gastos_buscarchasis", { q }).then(({ status, data }) => {
      if (status === 200) setChasisOpciones(data?.productos || []);
    });
  }

  async function guardar() {
    if (!fecha) { alert("Selecciona una fecha"); return; }
    if (!categoria) { alert("Selecciona una categoría"); return; }
    const valorNum = parseFloat(valor);
    if (Number.isNaN(valorNum) || valorNum <= 0) { alert("Ingresa un valor válido"); return; }
    if (!sucursal) { alert("Selecciona una sucursal"); return; }
    if (requiereChasis && !chasis) { alert("Esta categoría requiere indicar el chasis"); return; }

    const sucCode = String(sucursal?.label || sucursal || "").split("-")[0];

    setSaving(true);
    try {
      const { status, data } = await post("/api/agregargasto", {
        fecha: fecha,
        categoria: categoria,
        descripcion: descripcion,
        valor: valorNum,
        sucursal: sucCode,
        chasis: requiereChasis ? chasis.chasis : "",
        formapago: formaPago || "",
      });
      setSaving(false);
      if (status === 200) {
        // Igual que ares5510: al registrarlo sale su comprobante. Desde el
        // informe se puede reimprimir con los mismos datos.
        imprimirComprobanteGasto({
          id: data?.id,
          fecha: fecha.split("-").reverse().join("/"),
          tipogastos: categoria,
          descripcion: requiereChasis ? `${descripcion} (Chasis ${chasis.chasis})`.trim() : descripcion,
          valor: valorNum,
          sucursal: sucursal?.label || sucCode,
        });
        alert("Gasto registrado correctamente");
        if (onSaved) onSaved();
        if (embedded) {
          if (onExit) onExit();
        } else {
          navigate("/gastos/informegastos");
        }
      } else {
        alert(data?.Error || "No se pudo guardar el gasto");
      }
    } catch (err) {
      setSaving(false);
      alert("Error de red: " + err.message);
    }
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Gastos"
        subtitle="Agregar Gasto"
        cta={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => (embedded ? onExit && onExit() : navigate(-1))}
            sx={{
              borderColor: FP.color.border,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
            }}
          >
            Volver
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, maxWidth: 900, mx: "auto" }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Datos del gasto
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Fecha"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={categoriasList}
                  value={categoria || null}
                  onChange={(e, v) => { setCategoria(v); setChasis(null); }}
                  renderInput={(params) => (
                    <TextField {...params} label="Categoría" InputLabelProps={{ shrink: true }} sx={fpInputSx} />
                  )}
                />
              </Grid>
              {formasPago.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={formasPago}
                    value={formaPago}
                    onChange={(e, v) => setFormaPago(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Forma de pago"
                        helperText="Para la contabilidad; vacío cuenta como efectivo"
                        InputLabelProps={{ shrink: true }}
                        sx={fpInputSx}
                      />
                    )}
                  />
                </Grid>
              )}
              {requiereChasis && (
                <Grid item xs={12}>
                  <Autocomplete
                    options={chasisOpciones}
                    value={chasis}
                    onChange={(e, v) => setChasis(v)}
                    onInputChange={(e, texto, razon) => razon === "input" && buscarChasis(texto)}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(o, v) => o.chasis === v.chasis}
                    getOptionLabel={(o) =>
                      [o.chasis, [o.marca, o.modelo, o.colore].filter(Boolean).join(" ")]
                        .filter(Boolean)
                        .join(" · ")
                    }
                    noOptionsText="Escribe al menos 3 caracteres del chasis"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Chasis"
                        helperText="Esta categoría liga el gasto a un motor"
                        InputLabelProps={{ shrink: true }}
                        sx={fpInputSx}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Descripción"
                  fullWidth
                  multiline
                  rows={2}
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Valor"
                  fullWidth
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  disablePortal
                  options={sucursalList}
                  value={sucursal || null}
                  onChange={(e, v) => setSucursal(v)}
                  renderInput={(params) => (
                    <TextField {...params} label="Sucursal" InputLabelProps={{ shrink: true }} sx={fpInputSx} />
                  )}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => (embedded ? onExit && onExit() : navigate(-1))}
                disabled={saving}
                sx={{
                  borderColor: FP.color.border,
                  color: FP.color.textSecondary,
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.5,
                  "&:hover": { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                onClick={guardar}
                disabled={saving}
                sx={{
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.8,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                }}
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Agregargasto;
